import { Injectable } from '@angular/core';
import { Observable, defer, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import {
  PortalRouting,
  shouldRedirectAuthenticatedVendor
} from '../../vendor-redirect';
import { vendorsService } from '../shared/vive-api/vendors.service';
import { LOAD_BALANCER_TIMEOUT_MS } from '../shared/vive-api/vive-api.config';

/**
 * Reads a signed-in vendor's portal-routing inputs for the in-app redirect
 * guard. The pre-bootstrap redirect (`main.ts`) only runs once at page load;
 * this service backs `VendorPortalRedirectGuard`, which re-applies the full
 * redirect decision on every in-app navigation so a cut-over vendor can never
 * stay inside the legacy SPA. Same endpoint and decision as `main.ts`.
 *
 * The caller passes the vendor id: AuthService injects this service (to
 * `reset()` it), so this service cannot read AuthService back.
 *
 * Caching is per-vendorId and never caches failures:
 *  - keyed by vendorId so a different vendor signing in (this is a singleton
 *    that outlives logout) can never read another vendor's cached flag;
 *  - the entry is evicted if the fetch errors, so a transient 500 can't poison
 *    the cache for the rest of the page lifetime (the next navigation retries);
 *  - `reset()` clears everything when a session is seeded or cleared, so a
 *    server-side flag flip is picked up on the vendor's next sign-in.
 */
@Injectable({
  providedIn: 'root'
})
export class PortalRoutingService {
  private readonly cache = new Map<number, Observable<PortalRouting>>();

  /**
   * Whether this vendor should be forced into the new portal: the sticky
   * `useVendorPortal` flag OR the `vendor-app-redirect` PostHog rollout (flag
   * enabled + the vendor's CompanyId in the payload). The caller passes the
   * PostHog flag/payload so this service stays a pure data fetcher. Fails open
   * (false → stay in the legacy app) on any fetch error, so a transient API
   * problem never traps a vendor on a blank page.
   */
  public shouldForceVendorPortal(
    vendorId: number,
    flagEnabled: boolean,
    payload: string[] | null
  ): Observable<boolean> {
    return this.fetchPortalRouting(vendorId).pipe(
      map((routing) =>
        shouldRedirectAuthenticatedVendor(routing, flagEnabled, payload)
      ),
      catchError(() => of(false))
    );
  }

  /** Drop all cached routing so the next check re-fetches. */
  public reset(): void {
    this.cache.clear();
  }

  private fetchPortalRouting(vendorId: number): Observable<PortalRouting> {
    let cached = this.cache.get(vendorId);
    if (!cached) {
      // Cold, like the HttpClient call it replaced: the request starts when
      // the guard subscribes. No loading mask: this is a silent background
      // check on navigation, not a user-initiated request.
      cached = defer(() =>
        vendorsService.getPortalRouting({
          pathParams: { vendorId },
          options: {
            context: { SkipLoadingMask: true },
            requestTimeout: LOAD_BALANCER_TIMEOUT_MS
          }
        })
      ).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        // shareReplay replays errors too — evict so a transient failure is
        // never cached past the navigation that hit it.
        tap({ error: () => this.cache.delete(vendorId) })
      );
      this.cache.set(vendorId, cached);
    }
    return cached;
  }
}
